import React, { useState, useRef } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import ColorWrapper from '@styles/colors';
import { SmallLoading } from '@components/loading';
import { JOB_STATUSES } from '@helpers';
import { formatDateConditionally } from '@v2/helpers/time';
import ProductionOperationTimeChart from '@v2/context/ProductionOperation/timeChart';
import { useColumnContext } from '@v2/pages/planboard/components/Board/web/column-context';
import TouchableElement from '@components/common/touchableElement';
import { usePlanboardJobNavigation } from '@v2/pages/planboard/hooks/usePlanboardJobNavigation';
import useJobStatusBadgeConfig from '@v2/pages/planboard/hooks/useJobStatusBadgeConfig';
import useResetPriority from '@v2/pages/planboard/hooks/useResetPriority';
import JobCardDaysBadge from './JobCardDaysBadge';
import PriorityAdjustmentTooltip from './JobCardDetails/PriorityAdjustmentTooltip';
import { TourTarget } from '@components/tours';

import { JobCardFooterProps } from '@v2/pages/planboard/types';
import StatusBadge from '@components/common/StatusBadge';
import TimeDisplay from '@components/common/TimeDisplay';

const JobCardFooter: React.FC<JobCardFooterProps> = ({ scale, font, C, item, isPending, isCardHovered, onHoverChange }) => {
  const { columnId } = useColumnContext();
  const { navigateToGeneral, navigateToBatch } = usePlanboardJobNavigation(item);
  const isRunning = item.status === JOB_STATUSES.RUNNING;
  const jobStatusConfig = useJobStatusBadgeConfig({ C, status: item.status });
  const { resetPriority, isResetting } = useResetPriority();

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isLocalHovered, setIsLocalHovered] = useState(false);
  const badgeRef = useRef<View>(null);

  const isFooterHovered = isLocalHovered || isCardHovered;

  const operationProgress = (() => {
    const progressBaseline = item.totalTimeScheduledSeconds;

    const progress = item.totalTimeActualSeconds ?? 0;

    if (!progressBaseline || !progress) {
      return 0;
    }

    return Math.max(0, Math.min(2, progress / progressBaseline));
  })();

  const isLinkedToMachine = columnId !== -1;
  const isPaused = item.status === JOB_STATUSES.PAUSED;
  const isNotStarted = item.status === JOB_STATUSES.NOT_STARTED;
  const hasStarted = (item.totalTimeActualSeconds ?? 0) > 0;
  const finishedDate = item.finishedOn;
  const hasExpectedStart = !!item.expectedStartOn;

  const rightDate = (() => {
    if (finishedDate) return finishedDate;
    if (!isLinkedToMachine && !hasExpectedStart) return item.scheduledStartDate;
    return item.expectedStartOn || item.scheduledStartDate;
  })();

  const rightDateDisplay = formatDateConditionally(rightDate);

  const handleBadgePress = () => {
    setIsTooltipOpen(true);
  };

  const handleTooltipClose = () => {
    setIsTooltipOpen(false);
  };

  const handleResetPriority = async () => {
    // columnId from useColumnContext is the machineId for the current column
    await resetPriority(item.productionOperationId, columnId);
    setIsTooltipOpen(false);
  };

  return (
    <>
      <TourTarget tourId="planboard-card-footer">
        <TouchableElement onPress={isRunning ? navigateToBatch : navigateToGeneral}>
          {({ hovered }) => (
          <View
            style={{
              backgroundColor: hovered ? C('Background/Light/Field [web] hover') : 'transparent',
              paddingTop: scale(5),
              paddingBottom: scale(9),
              paddingHorizontal: scale(9),
            }}
            onPointerEnter={() => {
              setIsLocalHovered(true);
              onHoverChange?.(true);
            }}
            onPointerLeave={() => {
              setIsLocalHovered(false);
              onHoverChange?.(false);
            }}
          >
            {/* Row 1: Badge + Pin + Date (left) | Status badge (right) */}
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: scale(6),
                }}
              >
                {/* Badge with Pin icon - only for jobs assigned to a machine */}
                {isLinkedToMachine && (
                  <View ref={badgeRef}>
                    <JobCardDaysBadge
                      scheduledStartDate={item.scheduledStartDate}
                      expectedStartOn={item.expectedStartOn}
                      // showPinIcon={!!item.totalTimeScheduledSeconds} // TODO: Enable when backend adds isPinned field
                      isPending={isPending}
                      scale={scale}
                      font={font}
                      C={C}
                      hasPriorityOverride={item.hasPriorityOverride}
                      scheduledStartDateOverride={item.scheduledStartDateOverride}
                      onBadgePress={handleBadgePress}
                      isHighlighted={isFooterHovered}
                    />
                  </View>
                )}
                {/* Date - shows "Recalculating..." when pending */}
                {isPending ? (
                  <>
                    <Text style={[font['Body'], { color: C('Label/Light/Tertiary') }]}>{i18n.t('Recalculating...')}</Text>
                    <SmallLoading color={C('Label/Light/Tertiary')} />
                  </>
                ) : (
                  <Text style={[font['Body'], { color: C('Label/Light/Primary') }]}>{rightDateDisplay}</Text>
                )}
              </View>
              {/* Status badge on right - only for jobs assigned to a machine (not shown for Not Started temporarily) */}
              {isLinkedToMachine && !isNotStarted && <StatusBadge C={C} config={jobStatusConfig} scale={scale} font={font} />}
            </View>

            {/* Row 2: Time progress + Description */}
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: scale(4),
                marginTop: scale(4),
              }}
            >
              {/* Time chart - only for jobs assigned to a machine that have started */}
              {isLinkedToMachine && hasStarted && (
                <ProductionOperationTimeChart
                  size={18}
                  mainBgColor={isPaused ? 'ProdJobBatch/Light/Paused' : 'Status/Light/Productive'}
                  scale={scale}
                  secondaryBgColor="System/Light/Red"
                  progress={operationProgress}
                />
              )}
              <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: scale(4), flex: 1 }}>
                <TimeDisplay
                  withHMSFormat={false}
                  C={C}
                  value={item.totalTimeScheduledSeconds ?? 0}
                  scale={scale}
                  font={font}
                  fontForLetter="Footnote"
                  fontForSecondLetter="Footnote"
                  fontForDigit="Time 2"
                  fontForSecondDigit="Time 2"
                  includeSecondsAlways={false}
                  color={C('Label/Light/Primary')}
                  colorAlreadyParsed
                />
                <Text style={[font['Footnote'], { color: C('Label/Light/Tertiary'), flex: 1 }]} numberOfLines={1}>
                  {item.partDescription}
                </Text>
              </View>
            </View>

            {/* Row 3 (conditional): Operation name for No Machine column only */}
            {!isLinkedToMachine && (
              <Text style={[font['Subhead 1'], { marginTop: scale(4), color: C('Label/Light/Primary') }]} numberOfLines={1}>
                {item.operationName}
              </Text>
            )}
          </View>
          )}
        </TouchableElement>
      </TourTarget>

      {/* Priority Adjustment Tooltip */}
      <PriorityAdjustmentTooltip
        isOpen={isTooltipOpen}
        onClose={handleTooltipClose}
        item={item}
        anchorRef={badgeRef}
        onResetPriority={handleResetPriority}
        isResetting={isResetting}
        scale={scale}
        font={font}
      />
    </>
  );
};

export default ColorWrapper(JobCardFooter);
